/**
 * Centralised SEO/metadata helpers for DoppelCrush.
 *
 * Every public page should render a <SEO /> block at the top with the
 * route-specific title/description. The helper also emits the right
 * Open Graph, Twitter card and JSON-LD payloads so crawlers (Googlebot,
 * Bingbot, GPTBot, OAI-SearchBot, ClaudeBot, PerplexityBot, …) get a
 * complete picture without executing any of our React code.
 *
 * Visual design choices for the brand stay co-located in /index.css.
 */
import { Helmet } from "react-helmet-async";

// Canonical production origin. Change here to flip the whole site at once.
export const SITE_ORIGIN = "https://www.doppelcrush.com";
export const SITE_NAME = "DoppelCrush";
export const SITE_TAGLINE = "Find your Doppel. Flirt with chaos.";
export const SITE_DESCRIPTION =
  "DoppelCrush is a playful selfie-based dating and social discovery app. " +
  "Drop a selfie, get matched in Doppel Mode (twin energy) or Chaos Mode (plot twist energy), " +
  "and share your reveal with friends.";
// 1200x630 brand OG card — served by the same Pillow generator as twin cards
// but with a hardcoded inviter for the homepage. We point at a stable static
// asset for now (added under /public) so unfurlers can cache aggressively.
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.png`;

export function absoluteUrl(path = "/") {
  if (path.startsWith("http")) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * <SEO /> — drop into the top of any page component.
 *
 * Props:
 *   title         — page title (will be suffixed with the site name)
 *   description   — meta description (~155 chars sweet spot)
 *   path          — canonical pathname (e.g. "/how-it-works")
 *   image         — absolute OG image URL (defaults to the brand card)
 *   noIndex       — set true for pages we don't want in search results
 *   jsonLd        — optional schema.org JSON-LD object (or array of objects)
 *   ogType        — "website" by default, "article" for blog posts, etc.
 */
export function SEO({
  title,
  description = SITE_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  noIndex = false,
  jsonLd = null,
  ogType = "website",
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — ${SITE_TAGLINE}`;
  const canonical = absoluteUrl(path);
  const blobs = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data — one <script> per blob keeps Google happy. */}
      {blobs.map((blob, i) => (
        <script key={`${blob?.["@type"] || "ld"}-${i}`} type="application/ld+json">
          {JSON.stringify(blob)}
        </script>
      ))}
    </Helmet>
  );
}

// ---------------------------------------------------------------------------
// Reusable JSON-LD blobs
// ---------------------------------------------------------------------------
export const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logo512.png`,
  sameAs: [],
};

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  description: SITE_DESCRIPTION,
  publisher: ORGANIZATION_LD,
};

export const webPageLd = ({ title, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: title,
  description,
  url: absoluteUrl(path),
  isPartOf: { "@type": "WebSite", url: SITE_ORIGIN, name: SITE_NAME },
});

export const breadcrumbLd = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((step, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: step.name,
    item: absoluteUrl(step.path),
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
